import { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTranslationApp } from "../../lib/i18next";
import { getReactHookFormNestedError } from "../../utils/errors";
import { Controller, useController } from "react-hook-form";
import _ from "lodash";
import Button from "../Buttons/Button";
import {
  BUTTON_CREATE_TEMPLATE,
  BUTTON_EDIT,
  FIELD_REQUIRED,
  FIRST_USER_MESSAGE,
  GREETING,
  CUSTOMER_TYPE_QUICK_QUESTIONS_MESSAGE,
} from "../../i18n/keysTranslations";
import ModalMessageTemplate from "./ModalMessageTemplate";
import { BORDER_GRAY_COLOR, GRAY_50 } from "../../utils/colors";
import { BORDER_RADIUS } from "../../utils/constants";

const messageBlockSx = {
  mb: 1,
  py: 2,
  px: 1.5,
  width: "100%",
  borderRadius: BORDER_RADIUS,
  bgcolor: GRAY_50,
  border: `1px solid ${BORDER_GRAY_COLOR}`,
};

const messageTextSx = {
  lineHeight: 1.6,
  color: "text.primary",
};

const messageLabelSx = {
  fontWeight: 700,
};

const MessageTemplateContainer = ({
  name,
  control,
  errors = {},
  disabled = false,
}) => {
  const {
    field: { value, onChange },
  } = useController({
    control,
    name,
    defaultValue: "",
  });
  const [modalTemplateOpen, setModalTemplateOpen] = useState(false);
  const { t } = useTranslationApp();
  const error = getReactHookFormNestedError(errors, name);

  const onToggleModalTemplate = () => setModalTemplateOpen((prev) => !prev);

  const isEmptyTemplate = _.isEmpty(value);
  const isQuickQuestions = value?.customerTypeMessage === "quickQuestions";

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: {
          value: true,
          message: t(FIELD_REQUIRED),
        },
      }}
      render={() => (
        <Box id={name}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            {!isEmptyTemplate && (
              <Box sx={{ mt: 1, width: "100%" }}>
                <Box sx={messageBlockSx}>
                  <Typography variant="body2" sx={messageTextSx}>
                    <Box component="span" sx={messageLabelSx}>
                      {t(GREETING)}:
                    </Box>{" "}
                    {value?.greeting || "-"}
                  </Typography>
                </Box>

                {isQuickQuestions ? (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{ ...messageLabelSx, mb: 1 }}
                    >
                      {t(CUSTOMER_TYPE_QUICK_QUESTIONS_MESSAGE)}:
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.75,
                      }}
                    >
                      {(value?.customerTexts || []).map((text, index) => (
                        <Box
                          key={index}
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Box
                            sx={{
                              py: 0.75,
                              px: 1.5,
                              borderRadius: "16px",
                              border: `1px solid ${BORDER_GRAY_COLOR}`,
                              bgcolor: GRAY_50,
                              flex: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ lineHeight: 1.4 }}
                            >
                              {text || "-"}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ ...messageBlockSx, mb: 2 }}>
                    <Typography variant="body2" sx={messageTextSx}>
                      <Box component="span" sx={messageLabelSx}>
                        {t(FIRST_USER_MESSAGE)}:
                      </Box>{" "}
                      {value?.customerTexts?.[0] || "-"}
                    </Typography>
                  </Box>
                )}

                {!disabled && (
                  <Button
                    color="secondary"
                    type="button"
                    onClick={onToggleModalTemplate}
                  >
                    {t(BUTTON_EDIT)}
                  </Button>
                )}
              </Box>
            )}
            {isEmptyTemplate && (
              <Button
                color="secondary"
                type="button"
                onClick={onToggleModalTemplate}
                disabled={disabled}
              >
                {t(BUTTON_CREATE_TEMPLATE)}
              </Button>
            )}
          </Box>
          {error?.message && (
            <Typography variant="body2" color="error">
              {error.message}
            </Typography>
          )}
          {modalTemplateOpen && (
            <ModalMessageTemplate
              modalOpen={modalTemplateOpen}
              onCloseModal={onToggleModalTemplate}
              data={value}
              onCallbackSave={(data) => {
                onChange(data);
              }}
            />
          )}
        </Box>
      )}
    />
  );
};

export default MessageTemplateContainer;
